import Quill from "quill"
import { NO, UIColor, UIView, YES } from "uicore-ts"
import { RHColor } from "./RHColor"
import { RHFont } from "./Utils_slop/RHDesignTokens"
import { RHQuillSnowCSS } from "./Utils_slop/RHQuillSnowCSS"
import { RHTextSanitizer } from "./Utils_slop/RHTextSanitizer"

// Inject Quill's snow-theme CSS once globally (same pattern as RHDatePicker's
// embedded flatpickr CSS). The snow rules are scoped under .ql-* classes so they
// do not leak onto the rest of the app. The extra block wires Quill into the
// fixed-height UIView frame and lets the read-only description reuse Quill's
// formatting (lists, alignment) via the .ql-editor class without editor chrome.
UIView.injectCSS(RHQuillSnowCSS, "RHQuillSnowStyles")
UIView.injectCSS(`
.RHRichTextEditorContainer { position: absolute; inset: 0; display: flex; flex-direction: column; }
.RHRichTextEditorContainer > .ql-toolbar.ql-snow { flex: 0 0 auto; border: none; border-bottom: 1px solid ${RHColor.borderLight.stringValue}; font-family: ${RHFont.familyBase}; }
.RHRichTextEditorContainer > .ql-container.ql-snow { flex: 1 1 auto; min-height: 0; overflow: hidden; border: none; font-family: ${RHFont.familyBase}; font-size: 14px; }
.RHRichTextEditorContainer .ql-editor { overflow-y: auto; }
`)


/**
 * A rich-text editor field backed by Quill (snow theme), wrapped as a UIView so it
 * drops into the framework's frame-based layout for procurement and required-document descriptions.
 *
 * Value is HTML (Quill output). On the read-only side the same HTML is sanitised
 * with an allowlist (see RHTextSanitizer.sanitizedHTML) before rendering.
 */
export class RHRichTextEditorView extends UIView {

    static readonly _toolbarOptions = [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link"],
        ["clean"]
    ]

    _quill?: Quill
    _pendingHTML?: string
    _pendingPlainText?: string
    _placeholderText = ""
    _isRequired = NO
    _showsError = NO

    editorDidChange?: () => void

    constructor(elementID?: string) {
        super(elementID);
        (this as RHRichTextEditorView).configureWithObject({
            backgroundColor: UIColor.whiteColor,
            style: {
                border: `1px solid ${RHColor.borderLight.stringValue}`,
                borderRadius: "6px",
                overflow: "hidden"
            },
            controlEventTargetAccumulator: {
                PointerUpInside: (_sender, event) => {
                    this._restoreEditorFocusAfterPointerUp(event)
                }
            }
        })
    }

    _restoreEditorFocusAfterPointerUp(event: Event) {
        const pointerTarget = event.target
        if (!(pointerTarget instanceof Node) || !this._quill?.root.contains(pointerTarget)) {
            return
        }

        // UICore blurs the active element at the end of its root-view pointer-up
        // dispatch. Restore Quill after that dispatch has completed.
        queueMicrotask(() => {
            if (!this._quill?.root.contains(document.activeElement)) {
                this.focus()
            }
        })
    }


    // ─── Value API ────────────────────────────────────────────────────────────

    get html(): string {
        if (this._quill) {
            return this._quill.root.innerHTML
        }
        return this._pendingHTML ?? ""
    }

    set html(value: string) {
        this._pendingHTML = value
        this._pendingPlainText = undefined
        if (this._quill) {
            this._quill.setContents([])
            this._quill.clipboard.dangerouslyPasteHTML(value ?? "")
        }
    }

    /** Loads existing plain-text (legacy) descriptions as literal text, not HTML. */
    loadPlainText(text: string) {
        this._pendingPlainText = text
        this._pendingHTML = undefined
        if (this._quill) {
            this._quill.setText(text ?? "")
        }
    }

    get plainText(): string {
        if (this._quill) {
            return this._quill.getText().trim()
        }
        if (this._pendingPlainText !== undefined) {
            return this._pendingPlainText.trim()
        }
        return RHTextSanitizer.plainText(this._pendingHTML ?? "").trim()
    }

    get isEmpty(): boolean {
        return this.plainText.length === 0
    }


    // ─── Configuration ──────────────────────────────────────────────────────────

    set placeholderText(value: string) {
        this._placeholderText = value
        if (this._quill) {
            this._quill.root.dataset.placeholder = value
        }
    }

    set isRequired(value: boolean) {
        this._isRequired = value
    }

    get isRequired(): boolean {
        return this._isRequired
    }


    // ─── Validation ───────────────────────────────────────────────────────────

    validate(): boolean {
        let isValid = YES
        if (this._isRequired && this.isEmpty) {
            isValid = NO
        }
        this._applyErrorState(!isValid)
        return isValid
    }

    override focus() {
        this._quill?.focus()
    }

    _applyErrorState(shouldShow: boolean) {
        this._showsError = shouldShow
        if (shouldShow) {
            this.style.borderColor = RHColor.danger.stringValue
        }
        else {
            this.style.borderColor = RHColor.borderLight.stringValue
        }
    }


    override updateContentForCurrentEnabledState() {
        super.updateContentForCurrentEnabledState()
        this.hidden = NO
        this._quill?.enable(this.enabled)
    }


    // ─── Lifecycle: mount Quill once the element is in the DOM ───────────────────

    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        if (this._quill) {
            return
        }

        const quillWrapper = document.createElement("div")
        quillWrapper.className = "RHRichTextEditorContainer"
        this.viewHTMLElement.appendChild(quillWrapper)
        
        const quillContainer = document.createElement("div")
        quillWrapper.appendChild(quillContainer)

        this._quill = new Quill(quillContainer, {
            theme: "snow",
            readOnly: !this.enabled,
            placeholder: this._placeholderText,
            modules: { toolbar: RHRichTextEditorView._toolbarOptions }
        })

        if (this._pendingPlainText !== undefined) {
            this._quill.setText(this._pendingPlainText)
        }
        else if (this._pendingHTML) {
            this._quill.clipboard.dangerouslyPasteHTML(this._pendingHTML)
        }

        this._quill.on("text-change", () => {
            if (this._showsError) {
                this._applyErrorState(NO)
            }
            this.editorDidChange?.()
        })
    }

}
